import { useEffect, useState, useRef } from 'react';
import { SiteModel } from '../utils/models/site';
import Site from '../components/site';
import { getTestResults, saveTestResults } from '../utils/services/idbService';
import AppNav from '../components/app-nav';
import { Virtuoso } from 'react-virtuoso';
import Paper from '@mui/material/Paper';
import { useOfflineSwitch } from 'utils/helper/OfflineSwitchContext';
import { checkIfUserIsOwner } from 'utils/functions/checkSitePermission';
import { useDispatch, useSelector } from 'react-redux';
import { setAllSitesData, setAllSitesLoaded, updateSingleSite } from 'utils/redux/reducers/allSitesLoadedReducer';
import { setUserInformation } from 'utils/redux/reducers/userReducer';
import { IReduxState } from 'utils/models/redux-states';
import { IGroups } from 'utils/models/groups';

const OfflinePage = () => {
	const [siteList, setSiteList] = useState<SiteModel[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const username = localStorage.getItem('currentUser');
	const [siteData, setSiteData] = useState<any>({});
	const [siteDialogs, setSiteDialogs] = useState<Record<number, { open: boolean; tab: number }>>({});
  	const [testKitsUnits, setTestKitsUnits] = useState<{ [key: string]: any }>([]);
	const dispatch = useDispatch()
	const allSitesList = useSelector((state: any) => state.allSitesLoaded.allSitesData);
	const virtuosoRef = useRef<any>(null);
	const userInformationString = localStorage.getItem('user_information');
	const userInformation = userInformationString
		? JSON.parse(userInformationString)
		: null;
	const userId = userInformation ? userInformation.id : null;
	const reduxState = useSelector((state: IReduxState) => state.groupSites);

	useEffect(() => {
		async function fetchOfflineData() {
			if (username) {
				setLoading(true);
				let cachedData = await getTestResults(
					`allSites`,
					username
				);
				dispatch(setAllSitesData(cachedData));

				const siteArray = cachedData;
				setSiteList(siteArray);

				let newSiteData: any = {};
				for (let site of siteArray) {

					newSiteData[site.id] = site.sampleLocationAllVOs;
				}
				let allDescriptorsMap = await getTestResults(
					`allDataPointsUnits`,
					username
				);
				setSiteData(newSiteData);
				setTestKitsUnits(allDescriptorsMap[0])
				setLoading(false);

				//Try populating redux user from local storage
				dispatch(setUserInformation(userInformation));
				dispatch(setAllSitesLoaded(true));
				
			}
		}

		if (username) {
			fetchOfflineData();
		}
	}, [username]);
	const [isOffline, setIsOffline] = useState(!navigator.onLine);

	useEffect(() => {

		//Update data point offline cache
		let newSiteData: any = {};
		for (let site of allSitesList) {
			newSiteData[site.id] = site.sampleLocationAllVOs;
		}
		setSiteData(newSiteData);

		const _username = username ?? 'unknown';
		saveTestResults(
			`allSites`,
			allSitesList,
			_username
		);

	}, [allSitesList]);

	useEffect(() => {
		// This function updates the state based on online/offline status.
		const updateOnlineStatus = () => {
			setIsOffline(!navigator.onLine);
		};

		// Add event listeners
		window.addEventListener('online', updateOnlineStatus);
		window.addEventListener('offline', updateOnlineStatus);

		// Cleanup: remove event listeners on unmount to avoid memory leaks.
		return () => {
			window.removeEventListener('online', updateOnlineStatus);
			window.removeEventListener('offline', updateOnlineStatus);
		};
	}, []);

	useEffect(() => {
		// This is when someone changes the group from the top nav dropdown
		renderSitesFromSelectedGroup(reduxState.sitesInformation);
	}, [reduxState]);

	const renderSitesFromSelectedGroup = (selectedGroup: IGroups) => {
		if (selectedGroup.name === 'ALL SITES') {
			return setSiteList(allSitesList);
		}
		return handleGroupChange(selectedGroup);
	};

	const handleGroupChange = (groups: IGroups) => {
		if (!groups.sites) return setSiteList([]);

		const sortedGroupSites = [...groups.sites].sort(
			(a, b) => a.sortOrder - b.sortOrder
		);

		const result = [];
		for (const groupSite of sortedGroupSites) {
			for (const site of allSitesList) {
				const listToCompareId = site.id;
				const groupSiteId = groupSite.id;
				if (listToCompareId === groupSiteId) {
					result.push(site);
				}
			}
		}
		setSiteList(result);
	};

	const { offlineSwitchState } = useOfflineSwitch();
	const shouldRenderOffline = isOffline || !offlineSwitchState;

	const mapCachedData = async (cachedData: any, visibleColumns: any[]) => {

		const valueVOs = cachedData
		.filter((item: any) => !!item.new_entry) // Step 1: Filter items with new_entry
		.map((item: any) => {
			const visibleColumn = visibleColumns.find((col) => col.colName === item.displayName);
			return {
				displayName: item.displayName,
				outputAlarm: item.outputAlarm,
				outputId: item.outputId,
				outputValue: item.outputValue,
				visible: visibleColumn ?? false
			};
		})
		.filter(Boolean);
		
		const username = localStorage.getItem('userName') || 'unknown';
		const matchedData = cachedData.filter((data: any) => !!data.new_entry);
		const user = localStorage.getItem('currentUser') || 'unknown_user';
		const siteId = matchedData[0]?.siteId || 'unknown_site'; // Assuming siteId is available in the scope

		const normalizeCatchDataPoints = cachedData.map((dp: any) => {
			const { new_entry, ...rest } = dp;
			return rest;
		});
		await saveTestResults(`dataPoints_all_${siteId}`, normalizeCatchDataPoints, user);

		return {
			id: matchedData[0]?.new_entry,
			latestData: true,
			note: matchedData[0]?.note || "",
			operator: username,
			testKitId: matchedData[0]?.testKitId || 2,
			testKitName: matchedData[0]?.testkitName,
			timestamp: matchedData[0]?.timestamp || Date.now(),
			valueVOs
		};
	};
  const isSameLocationAndTestKit = (data1: any, data2: any) => {
	return data1.samplePointId === data2.samplePointId && data1.testkitName === data2.testkitName;
  };
  const processDataInSampleLocations = async (sampleLocations: any[], cachedData: any, visibleColumns: any[], targetSamplePointId: any) => {
	const updatedLocations = await Promise.all(sampleLocations.map(async (location) => {
  
	  if (location.id !== targetSamplePointId) {
		return location; 
	  }
  
	  const { dataPointAllVOs } = location;
  
	  for (let i = 0; i < dataPointAllVOs.length - 1; i++) {
		const currentData = dataPointAllVOs[i];
		const nextData = { ...dataPointAllVOs[i + 1] || {} };
  
		if (isSameLocationAndTestKit(currentData, nextData)) {
		  nextData.disabled = true; 
		}
	  }
  
	  let mappedData: any = {};
	  try {
		mappedData = await mapCachedData(cachedData, visibleColumns);
	  } catch (error) {
		console.error(error);
	  }
	  
	  const updatedData = [...dataPointAllVOs, mappedData];
	  return { ...location, dataPointAllVOs: updatedData };
	}));
  
	return updatedLocations;
  };
  const processData = async (allData: any[], siteId: any, cachedData: any) => {
	const siteData = allSitesList.find((site:any) => site.id === siteId);
	if (siteData) {
	  const { sampleLocationAllVOs, tableColumnSettingsAllVOs } = siteData;
	  
	  const visibleColumns = tableColumnSettingsAllVOs.filter((col: any) => col.visible);
	  // const updatedSampleLocations = processDataInSampleLocations(sampleLocationAllVOs, cachedData, visibleColumns, cachedData[0].samplePointId);
	  processDataInSampleLocations(sampleLocationAllVOs, cachedData, visibleColumns, cachedData[0].samplePointId)
	  .then(res => {
		dispatch(updateSingleSite({updatedSampleLocations:res,siteId}));
	 
		// Do something with the updated locations
	  })
	  .catch(error => {
		console.error(error);
	  });
	}
  };
	  const refreshCachedData = async (siteId: number) => {
		  try {
			  const _username = username ?? 'unknown';
			  const cachedData = await getTestResults(
				  `dataPoints_all_${siteId}`,
				  _username
			  );
			  if (cachedData && Array.isArray(cachedData)) {
				  processData(allSitesList, siteId, cachedData);
			  }
		  } catch (error) {
			  console.error(
				  `Error fetching cached data for site ${siteId}:`,
				  error
			  );
		  }
	  };

    const handleRunTestsClicked = (index: number) => {
      if (virtuosoRef.current) {
        requestAnimationFrame(() => {
            virtuosoRef.current.scrollToIndex({
              index: index,
              behavior: 'smooth',
            });
          
        });
      }
    };

	return (
		<div className="offline-container">
			<AppNav />
			{shouldRenderOffline && (
				<Paper
					elevation={1}
					style={{
						backgroundColor: '#F0F0F0',
						padding: '10px',
						textAlign: 'center',
						position: 'relative',
						zIndex: '100'
					}}
				>
					<span
						style={{
							fontFamily: 'Inter',
							fontWeight: '500',
							color: '#556670',
							fontSize: '14px',
						}}
					>
						Offline Mode:
					</span>
					<span
						style={{
							fontFamily: 'Jost',
							fontWeight: 'normal',
							color: '#556670',
							fontSize: '14px',
						}}
					>
						{' '}
						Your test results will save locally. Other features are limited.
					</span>
				</Paper>
			)}

			<Virtuoso
				computeItemKey={(index) => siteList[index]?.id ?? index}
				style={{ width: '100%', overflow: 'auto', height: 'calc(100vh - 52px)' }}
				totalCount={siteList.length}
				itemContent={(index) => {
					let site = siteList[index];
					const dataPoints = siteData[site.id] || [];
					return (
						<div className='site' style={{ marginTop: '70px' }}>
							<Site
								siteId={site.id}
								testKitsUnits={testKitsUnits}
								siteName={site.name}
								samplePoint={site?.sampleLocationAllVOs[0]}
								tableColumnSettingsAllVOs={site.tableColumnSettingsAllVOs}
								dataPoints={dataPoints}
								userId={userId}
								isLoading={loading}
								isOwner={checkIfUserIsOwner(site, userId)}
								isOffline={isOffline}
								onTestDone={() => refreshCachedData(site.id)}
								virtuosoRef={virtuosoRef}
								index={index}
								onRunTestsClicked={(index) => handleRunTestsClicked(index)}
								dialogOpen={siteDialogs[site.id]?.open ?? false}
								setDialogOpen={(open: boolean) => setSiteDialogs(prev => ({
									...prev,
									[site.id]: { tab: prev[site.id]?.tab ?? 0, open },
								}))}
								currentTab={siteDialogs[site.id]?.tab ?? 0}
								setCurrentTab={(tab: React.SetStateAction<number>) => setSiteDialogs(prev => {
									const cur = prev[site.id]?.tab ?? 0;
									return { ...prev, [site.id]: { open: prev[site.id]?.open ?? false, tab: typeof tab === 'function' ? tab(cur) : tab } };
								})}
							>
								<p>
									{dataPoints.length
										? `Latest data points: ${JSON.stringify(dataPoints)}`
										: 'No data available'}
								</p>
							</Site>
						</div>
					);
				}}
			/>
		</div>
	);
};

export default OfflinePage;
